/**
 * 「正在做」结束的**共享实现**（含跨天拆块）。
 *
 * <p>为什么抽成 utils：结束「在做」有两个人入口 ——</p>
 * <ul>
 *   <li>表格页时间块上的「结束」按钮（TimeTable.onEndRunning）</li>
 *   <li>定时提醒弹窗里的「结束」（TaskBlockPage.onTimerEnd，用户可能第二天才点）</li>
 * </ul>
 * <p>两处必须走同一条路，否则会一个拆块一个不拆。组件只负责 UI / 本地数据同步，
 * 接口编排都在这里。</p>
 *
 * <p><b>跨天语义</b>（用户 2026-10-13 定案）：块开始于 D0、用户在 D1（D1 > D0）才结束时 ——</p>
 * <ul>
 *   <li>D0 这一段：actualEndTime 置为 **D0 23:59:59**，占满当天；</li>
 *   <li>D0 之后到 D1 的每一天各建一个时间块：中间日 00:00:00 → 23:59:59（整天），
 *       D1 是 00:00:00 → 现在；</li>
 *   <li>后端按 plannedStartTime 的日期分桶，所以每一天自然落在星期表自己的列里。</li>
 * </ul>
 */

import { getTimeBlock, updateTimeBlockStatus, createTimeBlock } from '@/api/timeline.js'
import { updateFriendTimeBlockStatus, createFriendTimeBlock } from '@/api/share.js'

export const DAY_MINUTES = 1440

/** 本地时刻 → 'YYYY-MM-DDTHH:mm:ss'（不带时区，后端按 LocalDateTime 收） */
export function isoLocal(d) {
  const pad = n => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}` +
    `T${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`
}

/** Date → 'YYYY-MM-DD' */
export function dateStrOf(d) {
  const pad = n => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`
}

/** 某天 23:59:59 的本地 ISO（当天收尾用） */
export function dayEndIso(d) {
  return `${dateStrOf(d)}T23:59:59`
}

/**
 * 结束一个「在做」块。
 *
 * @param {Object} opts
 * @param {string|number} opts.id 时间块 id
 * @param {Object} [opts.block] 本地已有的块数据（含 actualStartTime / text / cat / color / projectId）；
 *        不给就按 id 拉后端详情（提醒弹窗那条路就只有定时条目，需要拉）
 * @param {string|number} [opts.friendOwnerId] 好友协同场景：传时间表所有者 id，改走好友接口
 * @returns {Promise<{sameDay:boolean, actualEndTime:string, created:Array, startDt:Date, now:Date, info:Object}>}
 *          created: [{ dateStr, id, startMin, endMin, params }]，供调用方按日期补本地数据
 */
export async function endRunningBlock({ id, block, friendOwnerId }) {
  let info = block || null
  if (!info) {
    const res = await getTimeBlock(id)
    if (!res || !res.success || res.code !== 200) {
      throw new Error((res && res.message) || '获取时间块失败')
    }
    info = res.data
  }
  const startDt = new Date(info.actualStartTime || info.plannedStartTime)
  if (isNaN(startDt.getTime())) throw new Error('时间块缺少开始时间，无法结束')
  const now = new Date()
  const sameDay = dateStrOf(startDt) === dateStrOf(now)
  // 跨天时今天这段收到「当天 23:59:59」；同日传 null，后端取当前时刻
  const endParam = sameDay ? null : dayEndIso(startDt)
  const res = friendOwnerId
    ? await updateFriendTimeBlockStatus(friendOwnerId, id, 'COMPLETED', endParam)
    : await updateTimeBlockStatus(id, 'COMPLETED', endParam)
  if (!res || !res.success || res.code !== 200) {
    throw new Error((res && res.message) || '结束失败')
  }
  const created = []
  if (!sameDay) created.push(...await splitIntoFollowingDays(info, startDt, now, friendOwnerId))
  return { sameDay, actualEndTime: sameDay ? isoLocal(now) : endParam, created, startDt, now, info }
}

/**
 * 给开始日之后的每一天各建一个时间块（整天块 + 今天到现在）。
 * 现在正好是 00:00 时今天的块是空的，直接跳过。
 */
async function splitIntoFollowingDays(info, startDt, now, friendOwnerId) {
  const created = []
  const today0 = new Date(now.getFullYear(), now.getMonth(), now.getDate())
  if (now.getTime() - today0.getTime() < 1) return created

  const dateStrs = []
  const cursor = new Date(startDt.getFullYear(), startDt.getMonth(), startDt.getDate() + 1)
  while (cursor.getTime() <= today0.getTime()) {
    dateStrs.push(dateStrOf(cursor))
    cursor.setDate(cursor.getDate() + 1)
  }
  const workspaceId = info.workspaceId || localStorage.getItem('taskblock-current-workspace-id')
  const projectId = info.projectId != null ? info.projectId : null
  const tagId = info.tagId != null ? info.tagId : null
  for (const dateStr of dateStrs) {
    const isLast = dateStr === dateStrOf(now)
    const startIso = `${dateStr}T00:00:00`
    const endIso = isLast ? isoLocal(now) : `${dateStr}T23:59:59`
    const startMin = 0
    const endMin = isLast ? now.getHours() * 60 + now.getMinutes() : DAY_MINUTES - 1
    if (endMin <= startMin) continue
    const params = {
      workspaceId,
      projectId,
      tagId,
      blockType: 'RECORD',
      plannedStartTime: startIso,
      plannedEndTime: endIso,
      actualStartTime: startIso,
      actualEndTime: endIso,
      executionStatus: 'COMPLETED',
      content: info.content || '',
      color: info.color || null
    }
    try {
      const r = friendOwnerId
        ? await createFriendTimeBlock(friendOwnerId, params)
        : await createTimeBlock(params)
      if (!r || !r.success || r.code !== 200) {
        throw new Error((r && r.message) || '创建跨天记录失败')
      }
      created.push({ dateStr, id: r.data ? r.data.id : null, startMin, endMin, params })
    } catch (e) {
      // 某一天建失败不该让整段结束回滚：今天那段已经收尾了，剩下的天数继续试
      console.error('[runningEnd] 创建跨天记录失败', dateStr, e)
      created.push({ dateStr, id: null, startMin, endMin, params, error: e.message || '创建失败' })
    }
  }
  return created
}
